import { sanitizeVariables } from './sanitize-input';
import { ApolloClient, InMemoryCache, from, HttpLink, ApolloLink, Observable } from '@apollo/client';
import { fromPromise } from '@apollo/client/link/utils';
import { onError } from '@apollo/client/link/error';
import { setContext } from '@apollo/client/link/context';
import { accessToken, refreshSession } from '../auth/session';
import { reportApiError } from '../errors/api-error';

export const createApolloClient = () => {
  const errorLink = onError(({ operation, graphQLErrors, networkError, forward }) => {
    const status = networkError?.statusCode;
    const authFailure = status === 401 || status === 403 || graphQLErrors?.some(error => ['UNAUTHENTICATED', 'FORBIDDEN'].includes(error.extensions?.code));
    if (authFailure && !operation.getContext().sessionRetried) {
      operation.setContext({ sessionRetried: true });
      return fromPromise(refreshSession().catch(error => { reportApiError(error); throw error; })).flatMap(() => forward(operation));
    }
    reportApiError({ graphQLErrors, networkError });
  });
  const authLink = setContext(async (_, { headers }) => {
    const token = await accessToken();
    return { headers: { ...headers, 'x-access-token': token } };
  });
  const inputLink = new ApolloLink((operation, forward) => {
    try { operation.variables = sanitizeVariables(operation.query, operation.variables); }
    catch (error) { return new Observable(observer => observer.error(error)); }
    return forward(operation).map(result => {
      const definition = operation.query.definitions.find(item => item.kind === 'OperationDefinition');
      if (definition?.operation === 'mutation' && !result.errors?.length && result.data && Object.values(result.data).some(value => value === null || value === false)) {
        const error = new Error('Mutation was not saved'); error.status = 400; throw error;
      }
      return result;
    });
  });
  // A bounded request prevents an unavailable API from leaving forms pending forever.
  const boundedFetch = async (uri, options) => {
    const abort = new AbortController();
    const timer = setTimeout(() => abort.abort(), 30000);
    const cancel = () => abort.abort();
    options.signal?.addEventListener('abort', cancel);
    try { return await fetch(uri, { ...options, signal: abort.signal }); }
    finally { clearTimeout(timer); options.signal?.removeEventListener('abort', cancel); }
  };
  return new ApolloClient({
    cache: new InMemoryCache({ addTypename: false }),
    link: from([errorLink, authLink, inputLink, new HttpLink({ uri: process.env.REACT_APP_URI, credentials: 'include', fetch: boundedFetch })]),
    defaultOptions: {
      watchQuery: { fetchPolicy: 'no-cache', errorPolicy: 'none' },
      query: { fetchPolicy: 'no-cache', errorPolicy: 'none' },
      mutate: { errorPolicy: 'none' },
    },
  });
};
